import React, { useMemo, useState } from 'react';
import { Button, Modal, Tag } from 'antd';
import { RocketOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import InfoNote from '@app/components/common/InfoNote/InfoNote';
import {
  getUnseenReleases,
  markReleasesSeen,
  readLastSeenVersion,
  RELEASE_TYPE_META,
} from './releases';

/**
 * Tự hiện MỘT lần cho mỗi bản phát hành mới: so version mới nhất với bản user
 * đã xem (localStorage). Đóng modal hoặc mở trang Nhật ký cập nhật đều đánh dấu đã xem.
 */
const WhatsNewModal: React.FC = () => {
  const navigate = useNavigate();
  const unseen = useMemo(() => getUnseenReleases(readLastSeenVersion()), []);
  // Người dùng lần đầu (chưa từng có mốc "đã xem") không cần popup — chỉ đánh dấu mốc.
  const isFirstVisit = readLastSeenVersion() === null;
  const [open, setOpen] = useState(unseen.length > 0 && !isFirstVisit);

  if (isFirstVisit) markReleasesSeen();

  const close = () => {
    markReleasesSeen();
    setOpen(false);
  };

  const viewAll = () => {
    close();
    navigate('/help/releases');
  };

  const latest = unseen[0];
  if (!latest) return null;

  return (
    <Modal open={open} onCancel={close} footer={null} width={520} destroyOnClose>
      <div className="tw-mb-3 tw-flex tw-items-center tw-gap-3">
        <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-accent-mint tw-text-lg tw-text-primary">
          <RocketOutlined />
        </span>
        <div>
          <div className="tw-text-base tw-font-semibold tw-text-on-surface">
            Có gì mới trong phiên bản {latest.version}
          </div>
          <div className="tw-text-xs tw-text-on-surface-variant">{latest.highlight}</div>
        </div>
      </div>

      <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
        {latest.items.map((item) => (
          <li key={item.title} className="tw-flex tw-items-start tw-gap-2">
            <Tag color={RELEASE_TYPE_META[item.type].color} className="tw-mt-0.5 tw-shrink-0">
              {RELEASE_TYPE_META[item.type].label}
            </Tag>
            <span className="tw-text-sm">
              <span className="tw-font-medium tw-text-on-surface">{item.title}</span>
              {item.description && <span className="tw-text-on-surface-variant"> — {item.description}</span>}
            </span>
          </li>
        ))}
      </ul>

      {unseen.length > 1 && (
        <InfoNote
          variant="info"
          className="tw-mt-3"
          items={`Còn ${unseen.length - 1} bản cập nhật trước đó bạn chưa xem — mở Nhật ký cập nhật để xem đầy đủ.`}
        />
      )}

      <div className="tw-mt-4 tw-flex tw-justify-end tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-surface-container-high tw-pt-3">
        <Button onClick={close}>Đóng</Button>
        <Button type="primary" onClick={viewAll}>
          Xem nhật ký cập nhật
        </Button>
      </div>
    </Modal>
  );
};

export default WhatsNewModal;
